import {
    Component,
    ContentChild,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output
} from "@angular/core";
import {FUIOptionsService, SelectionEvent} from "../controls/framework/options.service";
import {Subscription} from "rxjs";
import {FUISelectAction} from "../controls/select-action";
import {FUIOptionMetaData} from "../controls/select/options/option-meta-data";
import {PopParentHorizontalAlignment} from "../pops/abstract-pop";
import {FUIIconComponent} from "../icons/icon/icon.component";
import {FUIEventsService} from "../events/events.service";

@Component({
    providers: [
        FUIOptionsService
    ],
    selector: "fui-select-nav",
    styleUrls: ["./select-nav.component.scss"],
    templateUrl: "./select-nav.component.html"
})
export class FUISelectNavComponent implements OnDestroy, OnInit {
    /**
     * Optional additional action buttons
     */
    @Input() public actions: FUISelectAction[];

    @Input() public internallyFilterable: boolean;
    @Input() public valueField: string;
    @Input() public labelField: string;
    @Input() public loading: boolean;
    @Input() public total: number;
    @Input() public showAddOption: boolean;
    @HostBinding("class.extended") @Input() public showExtendedSearchOption: boolean;
    @Input() public id: string;
    @Input() public title: string;
    @Input() getOptionMetaData: (option: any) => FUIOptionMetaData = () => undefined;
    @Input() parentHorizontalAlignment: PopParentHorizontalAlignment = PopParentHorizontalAlignment.LEFT;

    @Output() public selected$: EventEmitter<any>;
    @Output() public extended$: EventEmitter<boolean>;
    @Output() public added$: EventEmitter<boolean>;

    /**
     * Emitted when an action is clicked
     */
    @Output() public action$: EventEmitter<FUISelectAction> = new EventEmitter<FUISelectAction>();

    @Output() public focussed$: EventEmitter<boolean>;
    @Output() public filtered$: EventEmitter<string>;

    @ContentChild(FUIIconComponent) public icon: FUIIconComponent;

    @HostBinding("class.focussed") public focussed: boolean;

    public tabindex: number;
    public filteredOptions: Array<any>;
    private _$click: Subscription;
    private clicking = false;

    constructor(public elementRef: ElementRef,
                protected eventsService: FUIEventsService,
                protected optionsService: FUIOptionsService) {
        this.selected$ = new EventEmitter<any>();
        this.added$ = new EventEmitter<boolean>();
        this.filtered$ = new EventEmitter<string>();
        this.extended$ = new EventEmitter<boolean>();
        this.focussed$ = new EventEmitter<boolean>();

        this.options = new Array<any>();
        this.internallyFilterable = true;
        this.filteredOptions = new Array<any>();
    }

    private _disabled: boolean;

    /**
     *
     * @returns {boolean}
     */
    get disabled(): boolean {
        return this._disabled;
    }

    /**
     *
     * @param {boolean} disabled
     */
    @HostBinding("class.disabled")
    @Input()
    set disabled(disabled: boolean) {
        this._disabled = disabled;
        if (this.disabled) {
            this.tabindex = -1;
        } else {
            this.tabindex = 0;
        }
    }

    private _options: Array<any>;

    /**
     *
     * @returns {Array<any>}
     */
    get options(): Array<any> {
        return this._options;
    }

    /**
     *
     * @param {Array<any>} options
     */
    @Input()
    set options(options: Array<any>) {
        if (options) {
            if (options.length != undefined) {
                this._options = options;
                this.filteredOptions = this._options;
            }
        }
    }

    private _popped: boolean;

    /**
     *
     * @returns {boolean}
     */
    get popped(): boolean {
        return this._popped;
    }

    /**
     *
     * @param {boolean} popped
     */
    set popped(popped: boolean) {
        this._popped = popped;
        this.focussed = popped;
        this.focussed$.emit(this.focussed);
    }

    private _query: string;

    /**
     *
     * @returns {string}
     */
    get query(): string {
        return this._query;
    }

    /**
     *
     * @param {string} query
     */

    set query(query: string) {
        if (this.query !== query) {
            this._query = query;

            if (this.internallyFilterable) {
                this.filteredOptions = [];
                this.options.forEach((option) => {
                    if (option[this.labelField].toLowerCase().includes(this._query.toLowerCase())) {
                        this.filteredOptions.push(option);
                    }
                })
            } else {
                this.filtered$.emit(this._query);
                this.filteredOptions = this.options;
            }
        }
    }

    ngOnInit(): void {
        this._$click = this.eventsService.click$.subscribe(
            (click) => {
                if (!this.clicking && this.popped && !this.elementRef.nativeElement.contains(click.target)) {
                    this.popped = false;
                }
            }
        )
    }

    ngOnDestroy(): void {
        this._$click.unsubscribe();
    }

    public close(): void {
        this.popped = false;
    }

    public open(): void {
        this.popped = true;
    }

    public onFocus(event?: any): void {
        this.focussed = true;
        this.focussed$.emit(this.focussed);
    }

    public onBlur(event?: any): void {
        this.focussed = false;
        this.focussed$.emit(this.focussed);
    }

    /**
     * Emit the selected value, and close the popup
     * @param event
     */
    public select(event: SelectionEvent): void {
        this.selected$.emit(event.selection);
        this.popped = false;
    }

    /**
     * Emit an event when the extended search button is clicked
     */
    public extend(): void {
        this.extended$.emit(true);
    }

    public add(): void {
        this.added$.emit(true);
    }

    /**
     * Click action for a select is different to other controls, it
     * only wants to pop up it's dropdown, not focus on the underlying input
     */
    @HostListener("click")
    public click(): void {
        if (this.disabled || this.popped) {
            return;
        }

        this.clicking = true;
        setTimeout(() => this.clicking = false);
        this.popped = true;
    }

    /**
     * If a user presses enter or space while focussed on the element,
     * use the click method to apply the relevant interaction
     * @param {KeyboardEvent} keyboardEvent
     */
    @HostListener("keypress", ["$event"])
    public keypress(keyboardEvent: KeyboardEvent) {
        if (!this.disabled) {
            if (keyboardEvent.code === "Enter" || keyboardEvent.code === "Space" || keyboardEvent.key === "Enter" || keyboardEvent.key === " ") {
                this.click();
                keyboardEvent.preventDefault();
                keyboardEvent.stopPropagation();
            }
        }
    }

    /**
     * Check if the list of options is empty
     * @returns {boolean}
     */
    public empty(options: Array<any>): boolean {
        return !this.loading && (!options || options.length === 0);
    }

    /**
     * Check if the list of options is non-empty
     * @returns {boolean}
     */
    public filled(options: Array<any>): boolean {
        return options && options.length > 0;
    }

    /**
     * Check if any of the items required for the
     * floating tool bar are included to decide
     * whether or not to show it
     * @returns {boolean}
     */
    public ftb(): boolean {
        return this.options.length > 0 && (this.showAddOption || this.showExtendedSearchOption || this.limited()) && !this.loading;
    }

    /**
     * Check if the returned options count
     * is less than the total number of items available.
     * @returns {boolean}
     */
    public limited(): boolean {
        return this.total && this.options && this.options.length < this.total;
    }

    /**
     * Checks if the list is filterable based on it's length
     * @returns {boolean}
     */
    public searchable(): boolean {
        return this.options && (!this.internallyFilterable || this.options.length >= 10);
    }

    /**
     *
     * @param {boolean} popped
     */
    public pop(popped: boolean): void {
        this.popped = popped;
    }

    /**
     * Invoked when an optional action button is clicked
     */
    public actionClicked(action: FUISelectAction): void {
        if(!action.disabled){
            this.action$.emit(action);
        }
    }
}
